import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Sigma from 'sigma';
import { EdgeAttributes, NodeAttributes } from './graphTypes';
import { useSocialGraphTrack } from './analytics';
import { ReactComponent as ZoomInIcon } from './assets/zoom-in.svg';
import { ReactComponent as ZoomOutIcon } from './assets/zoom-out.svg';
import { ReactComponent as FocusIcon } from './assets/focus.svg';
import { ReactComponent as FullscreenIcon } from './assets/fullscreen.svg';
import styles from './SocialGraphPOC.module.scss';

// Canvas action stack (Figma node 353-169): zoom in / zoom out / reset view /
// fullscreen, top-right of the canvas. Replaces react-sigma's ControlsContainer
// so it can live outside SigmaContainer — it drives the camera through the
// page's sigmaRef (same bridge the header export uses) and shifts left while
// the Information panel is open.
export const CanvasControls: React.FC<{
  sigmaRef: React.MutableRefObject<Sigma<NodeAttributes, EdgeAttributes> | null>;
  fullscreenTargetRef: React.RefObject<HTMLDivElement | null>;
  shifted: boolean;
}> = ({ sigmaRef, fullscreenTargetRef, shifted }) => {
  const { t } = useTranslation();
  const track = useSocialGraphTrack();
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', handleChange);
    return () => document.removeEventListener('fullscreenchange', handleChange);
  }, []);

  const camera = () => sigmaRef.current?.getCamera();

  const toggleFullscreen = () => {
    const entering = !document.fullscreenElement;
    track('sg_fullscreen_toggled', { fullscreen: entering });
    if (entering) {
      void fullscreenTargetRef.current?.requestFullscreen();
    } else {
      void document.exitFullscreen();
    }
  };

  return (
    <div className={`${styles.canvasControls} ${shifted ? styles.canvasControlsShifted : ''}`}>
      <button
        type="button"
        className={styles.canvasControlsBtn}
        onClick={() => camera()?.animatedZoom({ factor: 1.3, duration: 300 })}
        aria-label={t('Zoom in')}
        title={t('Zoom in')}
      >
        <ZoomInIcon aria-hidden="true" />
      </button>
      <button
        type="button"
        className={styles.canvasControlsBtn}
        onClick={() => camera()?.animatedUnzoom({ factor: 1.3, duration: 300 })}
        aria-label={t('Zoom out')}
        title={t('Zoom out')}
      >
        <ZoomOutIcon aria-hidden="true" />
      </button>
      <span className={styles.canvasControlsDivider} aria-hidden="true" />
      <button
        type="button"
        className={styles.canvasControlsBtn}
        onClick={() => camera()?.animatedReset({ duration: 300 })}
        aria-label={t('Reset view')}
        title={t('Reset view')}
      >
        <FocusIcon aria-hidden="true" />
      </button>
      <button
        type="button"
        className={styles.canvasControlsBtn}
        onClick={toggleFullscreen}
        aria-label={isFullscreen ? t('Exit full screen') : t('Full screen')}
        title={isFullscreen ? t('Exit full screen') : t('Full screen')}
      >
        <FullscreenIcon aria-hidden="true" />
      </button>
    </div>
  );
};
